import useLatestRequest from '../hooks/useLatestRequest';
import RefreshRegion from '../components/common/RefreshRegion';
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  BellIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XMarkIcon,
  EyeIcon,
  TrashIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import { useAuth } from '../contexts/AuthContext';
import { useHeaderSearch } from '../contexts/SearchContext';
import NotificationService from '../services/notification.service';
import LeaveService from '../services/leave.service';
import toast from '../utils/toast';
import { formatDateDisplay } from '../utils/dateDisplay';

const Notifications = () => {
  const { user } = useAuth();
  const { headerSearch } = useHeaderSearch();
  const [notifications, setNotifications] = useState([]);
  const [filter, setFilter] = useState('all');
  const { loading, begin: beginFetchNotifications } = useLatestRequest();

  // Ensure notifications is always an array
  const safeNotifications = Array.isArray(notifications) ? notifications : [];

  useEffect(() => {
    fetchNotifications();
  }, [filter]);

  const fetchNotifications = async () => {
    const request = beginFetchNotifications();
    try {
      const response = await NotificationService.getMyNotifications(filter);
      if (!request.isCurrent()) return;
      
      // Handle the new API response format
      if (response && response.success && Array.isArray(response.data)) {
        setNotifications(response.data);
      } else if (Array.isArray(response)) {
        // Fallback for old format
        setNotifications(response);
      } else {
        setNotifications([]);
      }
    } catch (error) {
      if (!request.isCurrent()) return;
      console.error('Error fetching notifications:', error);
      setNotifications([]);
    } finally {
      request.finish();
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      const response = await NotificationService.markAsRead(notificationId);
      if (response && response.success) {
        setNotifications(prev => 
          prev.map(notif => 
            notif.id === notificationId 
              ? { ...notif, isRead: true, readAt: new Date().toISOString() }
              : notif
          )
        );
        toast.success('Notification marked as read');
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
      toast.error('Failed to mark as read');
    }
  };

  const handleDelete = async (notificationId) => {
    try {
      const response = await NotificationService.deleteNotification(notificationId);
      if (response && response.success) {
        setNotifications(prev => prev.filter(notif => notif.id !== notificationId));
        toast.success('Notification deleted');
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
      toast.error('Failed to delete notification');
    }
  };

  const handleQuickAction = async (notification) => {
    try {
      switch (notification.actionType) {
        case 'approve_leave':
          await LeaveService.updateLeaveRequest(notification.actionId, { status: 'approved' });
          toast.success('Leave request approved');
          break;
        case 'view_profile':
          window.location.href = `/employees/${notification.actionId}`;
          break;
        case 'view_report':
          window.location.href = `/reports/${notification.actionId}`;
          break;
        default:
          toast.info('Action completed');
      }
      handleMarkAsRead(notification.id);
    } catch (error) {
      console.error('Error performing quick action:', error);
      toast.error('Failed to perform action');
    }
  };


  const getNotificationIcon = (type) => {
    switch (type) {
      case 'LEAVE': 
      case 'ACHIEVEMENT': return CheckCircleIcon;
      case 'ALERT': 
      case 'ATTENDANCE': return ExclamationTriangleIcon;
      case 'SYSTEM': return XMarkIcon;
      case 'WELCOME':
      case 'TEAM':
      case 'UPDATE':
      case 'REMINDER':
      default: return InformationCircleIcon;
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'LEAVE':
      case 'ACHIEVEMENT': return 'text-green-600 bg-green-100';
      case 'ALERT':
      case 'ATTENDANCE': return 'text-yellow-600 bg-yellow-100';
      case 'SYSTEM': return 'text-red-600 bg-red-100';
      case 'WELCOME':
      case 'TEAM':
      case 'UPDATE':
      case 'REMINDER':
      default: return 'text-blue-600 bg-blue-100';
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInHours < 48) return 'Yesterday';
    return formatDateDisplay(date);
  };

  const filterOptions = [
    { value: 'all', label: 'All', count: safeNotifications.length },
    { value: 'unread', label: 'Unread', count: safeNotifications.filter(n => !n.isRead).length },
    { value: 'LEAVE', label: 'Leave', count: safeNotifications.filter(n => n.type === 'LEAVE').length },
    { value: 'ATTENDANCE', label: 'Attendance', count: safeNotifications.filter(n => n.type === 'ATTENDANCE').length },
    { value: 'TEAM', label: 'Team', count: safeNotifications.filter(n => n.type === 'TEAM').length },
    { value: 'SYSTEM', label: 'System', count: safeNotifications.filter(n => n.type === 'SYSTEM').length }
  ];

  const filteredNotifications = safeNotifications.filter(notification => {
    if (filter !== 'all') {
      if (filter === 'unread' && notification.isRead) return false;
      if (filter !== 'unread' && notification.type !== filter) return false;
    }
    if (headerSearch) {
      const q = headerSearch.toLowerCase();
      return (
        (notification.title && notification.title.toLowerCase().includes(q)) ||
        (notification.message && notification.message.toLowerCase().includes(q)) ||
        (notification.type && notification.type.toLowerCase().includes(q))
      );
    }
    return true;
  });


  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-xl p-6 text-white"
      >
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">Notifications</h1>
            <p className="text-blue-100">Stay updated with all your important notifications</p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="text-right">
              <p className="text-2xl font-bold">{safeNotifications.filter(n => !n.isRead).length}</p>
              <p className="text-sm text-blue-200">Unread</p>
            </div>
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
              <BellIcon className="w-6 h-6" />
            </div>
          </div>
        </div>
      </motion.div>

      {/* Filter Tabs */}
      <Card>
        <Card.Body className="p-6">
          <div className="flex flex-wrap gap-4">
            {filterOptions.map((option) => (
              <Button
                key={option.value}
                variant={filter === option.value ? 'primary' : 'outline'}
                onClick={() => setFilter(option.value)}
                className="flex items-center space-x-2"
              >
                <span>{option.label}</span>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  filter === option.value ? 'bg-white text-blue-600' : 'bg-gray-100 text-gray-600'
                }`}>
                  {option.count}
                </span>
              </Button>
            ))}
          </div>
        </Card.Body>
      </Card>

      {/* Notifications List */}
      <RefreshRegion loading={loading}>
      <div className="space-y-4">
        {filteredNotifications.length > 0 ? (
          filteredNotifications.map((notification, index) => {
            const Icon = getNotificationIcon(notification.type);
            return (
              <motion.div
                key={notification.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.15 }}
              >
                <Card className={`hover:shadow-lg transition-all duration-300 ${
                  !notification.isRead ? 'ring-2 ring-blue-200 bg-blue-50' : ''
                }`}>
                  <Card.Body className="p-6">
                    <div className="flex items-start space-x-4">
                      {/* Icon */}
                      <div className={`p-2 rounded-full ${getNotificationColor(notification.type)}`}>
                        <Icon className="w-5 h-5" />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className={`text-lg font-semibold ${
                              !notification.isRead ? 'text-gray-900' : 'text-gray-700'
                            }`}>
                              {notification.title}
                            </h3>
                            <p className="text-gray-600 mt-1">{notification.message}</p>
                            
                            <div className="flex items-center space-x-4 mt-3">
                              <div className="flex items-center space-x-1 text-sm text-gray-500">
                                <ClockIcon className="w-4 h-4" />
                                <span>{formatTimestamp(notification.createdAt)}</span>
                              </div>
                              
                              {!notification.isRead && (
                                <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                                  New
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center space-x-2 ml-4">
                            {notification.actionType && notification.actionLabel && (
                              <Button
                                size="sm"
                                onClick={() => handleQuickAction(notification)}
                                className="whitespace-nowrap"
                              >
                                {notification.actionLabel}
                              </Button>
                            )}
                            
                            {!notification.isRead && (
                              <button
                                onClick={() => handleMarkAsRead(notification.id)}
                                className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                                title="Mark as read"
                              >
                                <EyeIcon className="w-4 h-4" />
                              </button>
                            )}
                            
                            <button
                              onClick={() => handleDelete(notification.id)}
                              className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                              title="Delete notification"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </motion.div>
            );
          })
        ) : (
          <Card>
            <Card.Body className="p-12 text-center">
              <BellIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Notifications</h3>
              <p className="text-gray-600">
                {filter === 'all' 
                  ? "You're all caught up! No notifications to show."
                  : `No ${filter} notifications found.`
                }
              </p>
            </Card.Body>
          </Card>
        )}
      </div>
      </RefreshRegion>

      {/* Quick Actions Footer */}
      {filteredNotifications.length > 0 && (
        <Card>
          <Card.Body className="p-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Showing {filteredNotifications.length} of {safeNotifications.length} notifications
              </div>
              <div className="flex space-x-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const unreadIds = safeNotifications.filter(n => !n.isRead).map(n => n.id);
                    unreadIds.forEach(id => handleMarkAsRead(id));
                  }}
                  disabled={safeNotifications.filter(n => !n.isRead).length === 0}
                >
                  Mark All as Read
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (window.confirm('Are you sure you want to clear all notifications?')) {
                      setNotifications([]);
                      toast.success('All notifications cleared');
                    }
                  }}
                >
                  Clear All
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default Notifications;
